import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ProductionApiError } from '../services/productionService';
import { getActiveSteps } from '../services/productionOpsService';
import type { ActiveStepRow } from '../types/production.types';

const PAGE_SIZE = 30;

export function useActiveSteps(workCenterId: string, branchId: string, search: string, readyOnly: boolean, enabled = true) {
  const { t } = useTranslation('production');
  const [items, setItems] = useState<ActiveStepRow[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [refreshedAt, setRefreshedAt] = useState<Date | null>(null);
  const generation = useRef(0);
  const pageRef = useRef(1);
  pageRef.current = page;

  const loadPage = useCallback(async (nextPage: number, append: boolean) => {
    const token = append ? generation.current : ++generation.current;
    if (append) setLoadingMore(true);
    else setLoading(true);
    try {
      const result = await getActiveSteps({
        workCenterId: workCenterId || undefined,
        branchId: branchId || undefined,
        search: search || undefined,
        readyOnly,
        page: nextPage,
        pageSize: PAGE_SIZE,
      });
      if (token !== generation.current) return;
      setItems((current) => (append ? [...current, ...(result.items ?? [])] : (result.items ?? [])));
      setTotalCount(result.totalCount ?? 0);
      setPage(nextPage);
      setRefreshedAt(new Date());
      setError(null);
    } catch (caught) {
      if (token !== generation.current) return;
      setError(caught instanceof ProductionApiError ? caught.message : t('ops.loadFailed'));
    } finally {
      if (token === generation.current) {
        setLoading(false);
        setLoadingMore(false);
      }
    }
  }, [workCenterId, branchId, search, readyOnly, t]);

  useEffect(() => {
    if (!enabled) return undefined;
    void loadPage(1, false);
    return undefined;
  }, [loadPage, enabled]);

  useEffect(() => {
    if (!enabled) return undefined;
    const timer = window.setInterval(() => {
      if (document.visibilityState === 'hidden') return;
      void loadPage(1, false);
    }, 30000);
    return () => window.clearInterval(timer);
  }, [loadPage, enabled]);

  return {
    items,
    totalCount,
    loading,
    loadingMore,
    error,
    refreshedAt,
    hasMore: items.length < totalCount,
    refresh: () => loadPage(1, false),
    loadMore: () => loadPage(pageRef.current + 1, true),
  };
}
